import AddIcon from "@atlaskit/icon/core/add";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import {useState} from "react";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {BottomDrawer} from "~/components/BottomDrawer";
import {toArgusHtmlDataAttributes} from "~/workspaceConfig/argusDomAttributes";

export type CallCreateDropdownItem = {
    label: string;
    onClick: () => void;
    dataAnalyticsEvent?: string | null;
    dataAnalyticsProps?: string | null;
};

/**
 * A standalone "Create" dropdown button for call event templates.
 * Desktop: add + "Create" + chevron. Mobile: add + chevron opens a bottom drawer.
 */
export function CallCreateDropdownButton({
    items,
    isMobile,
}: {
    items: Array<CallCreateDropdownItem>;
    isMobile: boolean;
}) {
    const [opened, setOpened] = useState(false);

    if (isMobile === true) {
        return (
            <>
                <AnankeButton
                    appearance="primary"
                    aria-label="Create"
                    onClick={() => {
                        setOpened(true);
                    }}
                    spacing="compact"
                    type="button"
                >
                    <span className="flex items-center gap-ds-space-050">
                        <AddIcon label="" size="small" />
                        <ChevronDownIcon label="" size="small" />
                    </span>
                </AnankeButton>
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Create"
                    zIndex={100}
                    fitContentOnOpen={false}
                    initialOpenHeight={480}
                >
                    <div className="grid gap-ds-space-100">
                        {items.map((item) => (
                            <AnankeButton
                                appearance="subtle"
                                iconAfter={ChevronRightIcon}
                                key={item.label}
                                onClick={() => {
                                    item.onClick();
                                    setOpened(false);
                                }}
                                shouldFitContainer={true}
                                type="button"
                                {...toArgusHtmlDataAttributes({
                                    dataAnalyticsEvent: item.dataAnalyticsEvent ?? null,
                                    dataAnalyticsProps: item.dataAnalyticsProps ?? null,
                                })}
                            >
                                {item.label}
                            </AnankeButton>
                        ))}
                    </div>
                </BottomDrawer>
            </>
        );
    }

    return (
        <AnankeDropdownMenu<HTMLButtonElement>
            placement="bottom-start"
            trigger={({triggerRef, ...triggerProps}) => (
                <AnankeButton
                    {...triggerProps}
                    appearance="primary"
                    aria-label="Create"
                    ref={triggerRef}
                    spacing="compact"
                    type="button"
                >
                    <span className="flex items-center gap-ds-space-075">
                        <AddIcon label="" size="small" />
                        <span className="whitespace-nowrap">Create</span>
                        <ChevronDownIcon label="" size="small" />
                    </span>
                </AnankeButton>
            )}
        >
            <AnankeDropdownItemGroup>
                {items.map((item) => (
                    <AnankeDropdownItem
                        dataAnalyticsEvent={item.dataAnalyticsEvent ?? null}
                        dataAnalyticsProps={item.dataAnalyticsProps ?? null}
                        key={item.label}
                        onClick={item.onClick}
                    >
                        {item.label}
                    </AnankeDropdownItem>
                ))}
            </AnankeDropdownItemGroup>
        </AnankeDropdownMenu>
    );
}
